import { useCallback, useMemo, useState } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import { Button, Card, Col, Container, Row, Tab, TabContainer } from 'react-bootstrap';
import {
	Bookmark,
	BookmarkFill,
	BoxArrowUpRight,
	Check,
	Dot,
	Download,
	Plus,
	Printer,
	Robot,
	Share,
	Star,
} from 'react-bootstrap-icons';
import { Helmet } from 'react-helmet';
import { constructUrl } from '@/lib/utils';
import { AdvisorModel, BOOKMARK_TYPE, DocumentModel } from '@/lib/models';
import { accountService, advisorService, documentService } from '@/lib/services';
import {
	useAlert,
	useCopyTextToClipboard,
	useHandleError,
	useNavigationConfig,
	useAnalytics,
	useAccount,
	useQuery,
} from '@/hooks';
import { AsyncPage, LoadingButton, Avatar, BlogRateModel, AspectRatioLockedImage, ImageModal } from '@/components';
import { ResearchDetailAiSummary } from '@/pages';
import React from 'react';
import classNames from 'classnames';
import { createUseStyles } from 'react-jss';

const useStyles = createUseStyles({
	'@global': {
		'@media print': {
			body: {
				visibility: 'hidden',
			},
			'.print-area, .print-area *': {
				visibility: 'visible',
			},
			'.print-area': {
				position: 'absolute',
				top: 0,
				left: 0,
			},
		},
	},
});

enum VIEW {
	INTRODUCTION = 'INTRODUCTION',
	AI_SUMMARY = 'AI_SUMMARY',
}

export const ResearchDetail = () => {
	useStyles();

	const query = useQuery();
	const navigate = useNavigate();
	const handleError = useHandleError();
	const { documentId } = useParams<{ documentId?: string }>();
	const { showAlert } = useAlert();
	const { account } = useAccount();
	const { navigationConfig } = useNavigationConfig();
	const analytics = useAnalytics();
	const { copyTextToClipboard } = useCopyTextToClipboard();

	const [document, setDocument] = useState<DocumentModel>();
	const [isBookmarking, setIsBookmarking] = useState(false);

	const view = useMemo(() => query.get('view') ?? VIEW.INTRODUCTION, [query]);
	const { pathname, search } = useLocation();
	const [showBlogRateModal, setShowBlogRateModal] = useState(false);
	const isHtmlDoc = useMemo(() => document?.contentSourceTypeId === 'AUTHOR', [document?.contentSourceTypeId]);
	const [isFollowing, setIsFollowing] = useState(false);
	const aiConversationIdNav = useMemo(() => query.get('aiConversationIdNav') ?? query.get('aiConversationId'), [
		query,
	]);

	const [imageModalSrc, setImageModalSrc] = useState('');

	const fetchData = useCallback(async () => {
		if (!documentId) {
			throw new Error('documentId is undefined');
		}

		const response = await documentService.getDocumentById(documentId).fetch();
		setDocument(response);
	}, [documentId]);

	const handleBookmarkButtonClick = useCallback(
		async (active: boolean, message: string) => {
			setIsBookmarking(true);

			try {
				if (!account) {
					throw new Error('account is undefined.');
				}

				if (!documentId) {
					throw new Error('documentId is undefined');
				}

				const { bookmark } = await accountService
					.updateAccountBookmarkContent(account.accountId, {
						bookmarkType: BOOKMARK_TYPE.DOCUMENT,
						contentId: documentId,
						activeFlag: active,
					})
					.fetch();

				setDocument(bookmark.bookmarkContent);

				showAlert({
					variant: 'success',
					children: <p className="mb-0 text-white">{message}</p>,
				});
			} catch (error) {
				handleError(error);
			} finally {
				setIsBookmarking(false);
			}
		},
		[account, documentId, handleError, showAlert]
	);

	const handlePrintButtonClick = useCallback(async () => {
		const originalTitle = window.document.title;

		if (document?.name) {
			const sanitizedTitle = document.name
				.replace(/[<>:"/\\|?*]/g, '')
				.replace(/\s+/g, '_')
				.replace(/[^\w\-_.]/g, '')
				.trim();
			window.document.title = sanitizedTitle;
		}

		window.print();
		window.document.title = originalTitle;

		try {
			if (!documentId) {
				throw new Error('blogId is undefined.');
			}

			// await blogsService.trackPrintActivity(blogId).fetch();
		} catch (error) {
			// fail silently on analytics calls
		}
	}, [documentId, document?.name]);

	const handleShareButtonClick = () => {
		copyTextToClipboard(window.location.href, 'Link copied to clipboard.');
	};

	const handleViewClick = useCallback(async () => {
		const { viewUrl } = document ?? {};
		if (!viewUrl) {
			return;
		}

		try {
			if (!documentId) {
				throw new Error('documentId is undefined');
			}

			await documentService.trackDocumentView(documentId).fetch();
		} catch (error) {
			// fail silently because we dont need to report a failed analytics call
		} finally {
			analytics.handleClickEvent(viewUrl);
			window.open(viewUrl, '_blank', 'noopener');
		}
	}, [analytics, document, documentId]);

	const handleDownloadClick = useCallback(async () => {
		const { downloadUrl } = document ?? {};

		if (downloadUrl) {
			try {
				if (!documentId) {
					throw new Error('documentId is undefined');
				}

				await documentService.trackDocumentDownload(documentId).fetch();
				analytics.handleClickEvent(downloadUrl);
			} catch (error) {
				// fail silently because we dont need to report a failed analytics call
			} finally {
				window.open(downloadUrl, '_blank', 'noopener');
			}
		}
	}, [document, documentId, analytics]);

	const handleFollowButtonClick = useCallback(
		async (author: AdvisorModel, follow: boolean) => {
			setIsFollowing(true);

			try {
				if (!document) {
					throw new Error('document is undefined.');
				}

				if (!author) {
					throw new Error('author is undefined.');
				}

				await advisorService.followAuthor({ authorId: author.advisorId, follow }).fetch();
				const blogResponse = await documentService.getDocumentById(document.documentId).fetch();

				setDocument(blogResponse);
			} catch (error) {
				handleError(error);
			} finally {
				setIsFollowing(false);
			}
		},
		[document, handleError]
	);

	const handleHtmlContentClick = useCallback((event: React.MouseEvent<HTMLDivElement>) => {
		const target = event.target as HTMLElement;
		if (target.tagName !== 'IMG') {
			return;
		}

		const img = target as HTMLImageElement;
		setImageModalSrc(img.src);
	}, []);

	return (
		<AsyncPage fetchData={fetchData}>
			<Helmet>
				<title>Hackett Connect - Research - Research Detail</title>
			</Helmet>

			{document && (
				<BlogRateModel
					blog={document}
					initialRating={document.rating?.rating}
					show={showBlogRateModal}
					onHide={() => {
						setShowBlogRateModal(false);
					}}
					onSubmit={(blogResponse) => {
						setDocument(blogResponse);
						setShowBlogRateModal(false);
					}}
				/>
			)}

			<ImageModal
				show={!!imageModalSrc}
				imageSrc={imageModalSrc}
				onHide={() => {
					setImageModalSrc('');
				}}
			/>

			<Container className="py-8 print-area">
				{query.get('ref') === 'chat' && (
					<Row className="mb-2">
						<Col>
							<p className="mb-0 text-muted">
								<Button
									className="mr-4 mb-0 p-0"
									variant="link"
									onClick={() => {
										navigate(
											constructUrl('/ask-hackett', {
												...(aiConversationIdNav && {
													aiConversationId: aiConversationIdNav,
												}),
											})
										);
									}}
								>
									AskHackett&trade;
								</Button>
							</p>
						</Col>
					</Row>
				)}
				<Row className="mb-5">
					<Col>
						<h5 className="mb-3 font-weight-regular text-muted">{document?.documentTypeName}</h5>
						<h1 className="mb-0">{document?.name}</h1>
						{(document?.authors ?? []).length > 0 && (
							<div className="d-flex flex-wrap mt-5">
								{document?.authors.map((author, authorIndex) => {
									const isLast = document?.authors.length - 1 === authorIndex;

									return (
										<Link
											to={`/advisory-team/${author.advisorId}`}
											className={classNames('d-inline-flex align-items-center', {
												'mr-2': !isLast,
											})}
										>
											<Avatar size={30} url={author.imageUrl} />
											<strong className="ml-2 small">{author.name}</strong>
										</Link>
									);
								})}
							</div>
						)}
					</Col>
					{isHtmlDoc && (
						<Col xl={5}>
							<AspectRatioLockedImage width={16} height={9} src={document?.image?.viewUrl} />
						</Col>
					)}
				</Row>
				<Row className="mb-8 py-3 border-top border-bottom align-items-center">
					<Col>
						<div className="d-flex align-items-center">
							<h5 className="mb-0 font-weight-regular text-muted">
								Published {document?.publicationDateDescription}
							</h5>
							{isHtmlDoc && document?.rating?.blogRatingId && (
								<div className="d-inline-flex align-items-center text-muted">
									<Dot className="mx-1" />
									<Star width={20} height={20} className="mr-1 text-primary" />
									<h5 className="mb-0">
										<strong>{document.rating.averageRating.toFixed(1)} / 5.0</strong>
									</h5>
								</div>
							)}
						</div>
					</Col>
					<Col>
						<div className="d-flex align-items-center justify-content-end">
							{document?.bookmarkedFlag ? (
								<LoadingButton
									isLoading={isBookmarking}
									className="p-2 text-primary"
									variant="link"
									onClick={() => {
										handleBookmarkButtonClick(false, 'Bookmark removed.');
									}}
								>
									<BookmarkFill className="d-flex" size={20} />
								</LoadingButton>
							) : (
								<LoadingButton
									isLoading={isBookmarking}
									className="p-2 text-primary"
									variant="link"
									onClick={() => {
										handleBookmarkButtonClick(true, 'Bookmark saved.');
									}}
								>
									<Bookmark className="d-flex" size={20} />
								</LoadingButton>
							)}
							{isHtmlDoc && navigationConfig?.allowDownloads && (
								<Button className="p-2 text-primary" variant="link" onClick={handlePrintButtonClick}>
									<Printer className="d-flex" width={20} height={20} />
								</Button>
							)}
							<Button className="p-2 mr-2 text-primary" variant="link" onClick={handleShareButtonClick}>
								<Share className="d-flex" size={20} />
							</Button>
							{view === VIEW.INTRODUCTION ? (
								<Button
									variant="primary"
									onClick={() => {
										navigate(constructUrl(pathname, { view: VIEW.AI_SUMMARY }, search), {
											replace: true,
										});
									}}
								>
									<Robot className="d-flex mr-2" size={20} />
									View AI Summary
								</Button>
							) : (
								<Button
									variant="outline-primary"
									onClick={() => {
										navigate(constructUrl(pathname, { view: VIEW.INTRODUCTION }, search), {
											replace: true,
										});
									}}
								>
									Exit AI Summary
								</Button>
							)}
						</div>
					</Col>
				</Row>
				<TabContainer activeKey={view} mountOnEnter unmountOnExit>
					<Tab.Content>
						<Tab.Pane eventKey={VIEW.INTRODUCTION}>
							<Row>
								<Col xl={7}>
									<Row className="mb-8">
										<Col>
											{isHtmlDoc ? (
												<>
													<div
														className="html-editor mb-8"
														dangerouslySetInnerHTML={{
															__html: document?.content ?? '',
														}}
														onClick={handleHtmlContentClick}
													/>
												</>
											) : (
												<>
													<h2 className="mb-6">Introduction</h2>
													<div
														className="html-editor mb-8"
														dangerouslySetInnerHTML={{
															__html: document?.description ?? '',
														}}
													/>
													{document?.podcastUrl && (
														<iframe
															src={document.podcastUrl}
															width="100%"
															height="200"
															title={document.name}
														/>
													)}
												</>
											)}

											<hr className="mb-8" />
											{!isHtmlDoc && (
												<h5 className="mb-8 font-weight-regular text-muted">
													{document?.topicTags.map((tag, tagIndex) => {
														const isLast =
															(document?.topicTags ?? []).length - 1 === tagIndex;
														return (
															<React.Fragment key={tag.tagId}>
																<Link to={`/search?query=${tag.name}`}>{tag.name}</Link>
																{!isLast && <span>,&nbsp;</span>}
															</React.Fragment>
														);
													})}
												</h5>
											)}
											{isHtmlDoc && document?.allowRating && (
												<>
													<Button
														className="mb-10 pdf-ignore"
														variant="outline-primary"
														size="sm"
														onClick={() => {
															setShowBlogRateModal(true);
														}}
													>
														Rate Post
													</Button>
													{document?.ctaButtonText && (
														<Button
															className="ml-2"
															variant="secondary"
															size="sm"
															onClick={() => {
																window.open(
																	document.ctaButtonUrl,
																	'_blank',
																	'noopener'
																);
															}}
														>
															{document?.ctaButtonText}
														</Button>
													)}
												</>
											)}
											{/* <div className="border bg-white p-4">
												<h4 className="mb-4 text-muted">Featured Company</h4>
												<h4 className="mb-4">
													<strong>Company Name</strong>
												</h4>
												<Button
													onClick={() => {
														// navigate('/#');
													}}
												>
													Learn More
												</Button>
											</div> */}
										</Col>
									</Row>
								</Col>
								<Col xl={{ span: 4, offset: 1 }}>
									{document && (
										<>
											{!document.podcastUrl && document.viewUrl && (
												<Button
													className="mb-3 d-flex align-items-center w-100"
													variant="outline-primary"
													onClick={handleViewClick}
												>
													<BoxArrowUpRight className="mr-2" width={16} height={16} />
													View Full Document
												</Button>
											)}
											{navigationConfig?.allowDownloads &&
												document?.canDownload &&
												document?.downloadUrl && (
													<Button
														className="d-flex align-items-center w-100"
														variant="outline-primary"
														onClick={handleDownloadClick}
													>
														<Download className="mr-2" width={20} height={20} />
														Download Document
													</Button>
												)}
										</>
									)}
									{document?.authors.map((author) => {
										return (
											<Card className="mb-6">
												<Card.Body>
													<h4 className="mb-4 text-muted text-center">About the Author</h4>
													{(author && (
														<div className="text-center">
															<Avatar
																className="mb-4 mx-auto"
																size={120}
																url={author.imageUrl}
															/>
															<h4 className="mb-0">{author.name}</h4>
															<h5 className="mb-4 font-weight-regular text-gray-700">
																{author.position}
															</h5>
															<hr className="mb-4" />
															<p className="mb-4 small">
																Follow the author to get notified when they post.
															</p>
															<div className="d-flex align-items-center justify-content-center">
																{author.userIsFollowing ? (
																	<LoadingButton
																		isLoading={isFollowing}
																		classNameInner="d-flex align-items-center"
																		className="pl-3 mr-1"
																		variant="outline-primary"
																		onClick={() =>
																			handleFollowButtonClick(author, false)
																		}
																	>
																		<Check
																			className="mr-2"
																			width={20}
																			height={20}
																		/>
																		Following
																	</LoadingButton>
																) : (
																	<LoadingButton
																		isLoading={isFollowing}
																		classNameInner="d-flex align-items-center"
																		className="pl-3 mr-1"
																		variant="primary"
																		onClick={() =>
																			handleFollowButtonClick(author, true)
																		}
																	>
																		<Plus className="mr-2" width={20} height={20} />
																		Follow
																	</LoadingButton>
																)}

																<Button
																	className="ml-1"
																	variant="outline-primary"
																	onClick={() => {
																		navigate(`/advisory-team/${author.advisorId}`);
																	}}
																>
																	View Bio
																</Button>
															</div>
														</div>
													)) ?? <p className="mb-0 text-center">No information available</p>}
												</Card.Body>
											</Card>
										);
									})}
								</Col>
							</Row>
						</Tab.Pane>
						<Tab.Pane eventKey={VIEW.AI_SUMMARY}>
							{document && (
								<ResearchDetailAiSummary
									documentTitle={document.name}
									documentId={document.documentId}
									allowAiIndexing={document.allowAiIndexing}
								/>
							)}
						</Tab.Pane>
					</Tab.Content>
				</TabContainer>
			</Container>
		</AsyncPage>
	);
};
